import { v4 as uuidv4 } from 'uuid';
import { ReportDesign, ReportType } from '../../models/end-client-reporting-package';
import { withMatrixExplainers } from './report-design-edit';

/**
 * The keys modules were stored under before they were renamed, and what each is called now.
 *
 * Every deck, template, archived version and published bundle written before the rename still
 * carries the old keys, and none of them is rewritten in place -- a design is read far more often
 * than it is saved, and a bulk rewrite of every stored document is a migration with no undo. So the
 * old key is translated wherever a design comes in, and the next save writes the new one.
 *
 * Mirrored by `EndClientReportTypeAliases` in the API, which translates the same keys before a
 * design reaches the chat agent: the agent's catalogue only knows the new ones. If a module is ever
 * renamed again, both lists grow -- an entry here is never removed while a stored deck might use it.
 */
export const LEGACY_REPORT_TYPES: Readonly<Record<string, ReportType>> = {
  'methodology': 'study-overview',
  'executive-summary': 'headline-stats',
  'recommended-actions': 'action-list',
  'single-kpi-dial': 'kpi-score',
  'multi-market': 'segment-tiles',
  'stacked-distribution': 'stacked-scale',
  'categorical-distribution': 'filter-comparison',
  'small-multiples': 'subgroup-panels',
  'data-table': 'crosstab',
  'head-to-head': 'diverging-bars',
  'ranked-driver': 'tiered-ranking',
  'ownership-heatmap': 'ownership-matrix',
  'relationship-ladder': 'stage-matrix',
  'explainer-panel': 'stage-explainer',
  'positioning-scatter': 'scatter-plot',
  'verbatim-explorer': 'open-end-explorer',
};

/**
 * The same design as this version reads it: every block's type under its current key, and any
 * explainer a stage matrix's settings ask for created beside it (see withMatrixExplainers -- the
 * report-generation agent turns an explainer on that way, since an explainer is not a module it can
 * add). Both happen here because this is where every design comes in.
 *
 * Returns the design untouched when nothing needed changing, so a deck saved since costs nothing to
 * read. Otherwise a copy: the design may be a cached object something else holds.
 */
export function withCurrentReportTypes<T extends ReportDesign | null | undefined>(design: T): T {
  return withMatrixExplainers(withCurrentKeys(design), uuidv4);
}

function withCurrentKeys<T extends ReportDesign | null | undefined>(design: T): T {
  if (!design?.sections || !hasLegacyType(design)) return design;

  const sections = Object.fromEntries(Object.entries(design.sections).map(([sectionId, section]) => [
    sectionId,
    {
      ...section,
      pages: Object.fromEntries(Object.entries(section.pages ?? {}).map(([pageId, page]) => [
        pageId,
        {
          ...page,
          pageSections: Object.fromEntries(Object.entries(page.pageSections ?? {}).map(([blockId, block]) => [
            blockId,
            { ...block, reportType: currentReportType(block.reportType) },
          ])),
        },
      ])),
    },
  ]));

  return { ...design, sections } as T;
}

/** A stored key under its current name; a key that was never renamed comes back as it went in. */
export function currentReportType(type: string): ReportType {
  return (LEGACY_REPORT_TYPES[type] ?? type) as ReportType;
}

function hasLegacyType(design: ReportDesign): boolean {
  return Object.values(design.sections).some(section =>
    Object.values(section.pages ?? {}).some(page =>
      Object.values(page.pageSections ?? {}).some(block => block.reportType in LEGACY_REPORT_TYPES)));
}
